// The local player during a match: input sampling, prediction, reconciliation, recoil, camera,
// viewmodel and HUD frame data. One instance lives as long as the session.

import { toJson } from "@bufbuild/protobuf";
import type { AudioEngine } from "../audio";
import type { Hud } from "../hud";
import type { HudFrame } from "../hud/types";
import type { GameConnection, SnapshotBuffer, TickClock } from "../net";
import { MAX_INPUTS_PER_PACKET, MAX_TICKS_PER_FRAME, TICK_RATE } from "../net/constants";
import { LoadoutSchema } from "../proto/gunrun/v1/common_pb";
import type { Loadout } from "../proto/gunrun/v1/common_pb";
import type { Spawned } from "../proto/gunrun/v1/game_pb";
import type { Renderer, ViewmodelState } from "../render";
import { hasDotSight } from "../render/models/attachments";
import { probe } from "../perf/probe";
import { settings } from "../settings/store";
import { weaponName } from "../state/meta";
import { Predictor, shotSeed, weaponStatsJson } from "../wasm";
import type { WorldHandle } from "../wasm";
import { Buttons } from "../wasm/types";
import type { InputCmdData, PlayerView, PredictedFrame, PredictedShot, SnapshotData } from "../wasm/types";
import { CameraRig } from "./camera";
import type { CameraPose } from "./camera";
import { DEG2RAD, clamp } from "./angles";
import { FOV_PULSE_DEG } from "./cameraMath";
import { freezeInput } from "./buttons";
import { HEAVY_LAND_SPEED, ShellInsertCue, ownStepVolume } from "./audioCues";
import { StrideTracker } from "./footsteps";
import type { FxDirector } from "./fx";
import type { InputController } from "./input";
import { Flinch, Recoil } from "./recoil";
import type { RecoilStep } from "./recoil";
import type { RoomState } from "./room";
import { spawnShieldLeft, shieldStart } from "./spawnShield";
import { SNAPSHOT_OUTCOME, TraceRecorder } from "./trace";
import { WeaponStatsCache } from "./weaponStats";

/**
 * Reused input commands, indexed by tick: `recent` keeps the last `MAX_INPUTS_PER_PACKET` of them
 * while a frame samples up to `MAX_TICKS_PER_FRAME` new ones, so a slot is never rewritten while it
 * is still to be sent.
 */
const RING = MAX_INPUTS_PER_PACKET + MAX_TICKS_PER_FRAME;
/** The lead controller is told the clock rate again when the pongs' estimate moved by this much. */
const RATE_RESEED = 0.01;
/** Animation lengths of the viewmodel actions that have no progress value in the sim (ms). */
const MELEE_ANIM_MS = 380;
const THROW_ANIM_MS = 700;
/** Fraction of a magazine reload after which the magazine-in sound plays. */
const MAG_IN_FRAC = 0.55;

export interface LocalDeps {
  renderer: Renderer;
  hud: Hud;
  audio: AudioEngine;
  fx: FxDirector;
  input: InputController;
  tickClock: TickClock;
  snapshots: SnapshotBuffer;
  room: RoomState;
}

interface LoadoutInfo {
  primary: string;
  secondary: string;
  lethal: string | null;
  tactical: string | null;
}

export class LocalPlayer {
  predictor: Predictor | null = null;
  alive = false;
  health = 100;
  spawnProtected = false;
  view: PlayerView | null = null;

  readonly recoil = new Recoil();
  /** Roll punch of the camera that follows every shot (draw only, the aim is not touched). */
  readonly flinch = new Flinch();
  readonly camera = new CameraRig();
  readonly stats = new WeaponStatsCache(weaponStatsJson);
  /** F8 stuck-trace rings (`trace.ts`); reachable through the perf probe as `objects.local.trace`. */
  readonly trace = new TraceRecorder();

  private spawnTick = 0;
  /** Tick the spawn protection timer started at (`shieldStart`). */
  private shieldStartTick = 0;
  private lastSnapshotTick = 0;
  /** The connection whose clock rate the lead controller was told, and the rate it was told. */
  private rateSeededFor: GameConnection | null = null;
  private seededRate = 1;
  private runSpeed = 7.6;
  private hopSpeedCap = 20;
  private loadout: LoadoutInfo = { primary: "", secondary: "", lethal: null, tactical: null };

  private readonly ring: InputCmdData[] = [];
  private readonly recent: InputCmdData[] = [];
  private readonly scratch: InputCmdData[] = [];
  private readonly recoilStep: RecoilStep = { pitch: 0, yaw: 0 };
  private readonly stride = new StrideTracker();

  // Per-frame accumulators for the viewmodel.
  private shotsThisFrame = 0;
  /** Largest view kick (degrees up) of the shots predicted this frame, for the viewmodel. */
  private shotKickThisFrame = 0;
  /**
   * Shots predicted this frame whose tracer and flash wait for the viewmodel update; `remoteTick` is
   * the remote time shown at the moment of the shot (what the server's lag compensation rewinds to).
   */
  private readonly pendingShots: { shot: PredictedShot; tick: number; remoteTick: number; seed: number; hideFlash: boolean }[] = [];
  /** The view offset of this frame's inputs, ticks. */
  private frameOffsetTicks = 0;
  private meleeStart = -1;
  private throwStart = -1;
  private magInPlayed = false;
  private prevSliding = false;
  private readonly shellCue = new ShellInsertCue();
  private lastStepX = 0;
  private lastStepZ = 0;
  private haveStep = false;

  private hudFps = 0;
  private hudPing = 0;
  private hudInfoAt = 0;
  private scopeShown = false;

  private readonly vm: ViewmodelState = {
    weaponId: "",
    attachments: [],
    camo: "none",
    visible: false,
    adsFrac: 0,
    reloadFrac: -1,
    switchFrac: -1,
    shotsThisFrame: 0,
    shotKickDeg: 0,
    kickPitchDeg: 0,
    kickYawDeg: 0,
    lookDeltaX: 0,
    lookDeltaY: 0,
    speed: 0,
    onGround: true,
    sliding: false,
    wallrunSide: 0,
    meleeFrac: 0,
    throwFrac: 0,
  };

  private readonly hudFrame: HudFrame = {
    health: 100,
    maxHealth: 100,
    ammo: 0,
    reserve: 0,
    magSize: 0,
    weaponName: "",
    slot: 0,
    lethal: null,
    lethalCount: 0,
    tactical: null,
    tacticalCount: 0,
    reloading: false,
    reloadFrac: 0,
    speed: 0,
    spreadDeg: 0,
    hFovDeg: 103,
    adsFrac: 0,
    fps: 0,
    pingMs: 0,
    showCrosshair: false,
    reticleDot: false,
    spawnShield: 0,
  };

  constructor(private readonly d: LocalDeps) {
    for (let i = 0; i < RING; i++) {
      this.ring.push({ tick: 0, buttons: 0, yaw: 0, pitch: 0, weaponSlot: 0, viewOffset: 0 });
    }
  }

  // ---- Lifecycle ----

  /** Builds the predictor for a loaded map. */
  attach(world: WorldHandle, moveConfigJson: string): void {
    this.detach();
    this.predictor = new Predictor(world, moveConfigJson);
    this.trace.begin(moveConfigJson);
    try {
      const cfg = JSON.parse(moveConfigJson) as { runSpeed?: number; hopSpeedCap?: number };
      if (typeof cfg.runSpeed === "number") this.runSpeed = cfg.runSpeed;
      if (typeof cfg.hopSpeedCap === "number") this.hopSpeedCap = cfg.hopSpeedCap;
    } catch {
      // Keep the defaults of DESIGN.md.
    }
  }

  detach(): void {
    this.predictor?.free();
    this.predictor = null;
    this.alive = false;
    this.view = null;
    this.recent.length = 0;
    this.pendingShots.length = 0;
  }

  /** The tick clock jumped: older inputs no longer line up with what the server wants. */
  onResync(): void {
    this.recent.length = 0;
  }

  /** Own `Spawned`: start predicting from the server's spawn state. */
  spawn(sp: Spawned, nowMs: number, conn: GameConnection): void {
    const predictor = this.predictor;
    const loadout = sp.loadout;
    if (!predictor || !loadout) return;
    predictor.spawn(JSON.stringify(toJson(LoadoutSchema, loadout)), sp.sim, sp.tick);
    this.spawnTick = sp.tick;
    this.shieldStartTick = shieldStart(sp.tick, this.d.room.phase, this.d.room.phaseEndTick);
    this.applyLoadout(loadout);
    this.d.input.resetForSpawn(sp.yaw);
    this.recoil.reset();
    this.flinch.reset();
    this.recent.length = 0;
    this.shotsThisFrame = 0;
    this.pendingShots.length = 0;
    this.meleeStart = this.throwStart = -1;
    this.magInPlayed = false;
    this.prevSliding = false;
    this.shellCue.reset();
    this.haveStep = false;
    this.health = 100;
    this.spawnProtected = true;
    const view = predictor.view();
    this.view = view;
    this.camera.reset(view);
    this.seedClockRate(conn);
    this.alive = true;
    this.d.room.me.alive = true;
    this.d.hud.death(null);
    this.d.audio.playSpawn();
    this.d.renderer.viewmodel.setLoadoutWeapon(this.loadout.primary, this.stats.attachmentsOf(this.loadout.primary), this.stats.camoOf(this.loadout.primary));
    // After the synchronous work above: the clock starts when the first frame can run, not before a hitch of the spawn itself.
    const startMs = Math.max(nowMs, performance.now());
    this.d.tickClock.start(Math.max(conn.clock.initialPredTick(startMs, this.d.tickClock.firstTickLead()), sp.tick + 1), startMs);
  }

  private applyLoadout(l: Loadout): void {
    const primary = l.primary;
    const secondary = l.secondary;
    this.stats.setLoadout(
      [
        { id: primary?.weapon ?? "", attachments: primary?.attachments ?? [], camo: primary?.camo ?? "none" },
        { id: secondary?.weapon ?? "", attachments: secondary?.attachments ?? [], camo: secondary?.camo ?? "none" },
        { id: "knife", attachments: [], camo: "none" },
      ],
      l.perks,
    );
    this.loadout = {
      primary: primary?.weapon ?? "",
      secondary: secondary?.weapon ?? "",
      lethal: l.lethal || null,
      tactical: l.tactical || null,
    };
  }

  /** The local player died (Killed message or leaving the map). */
  kill(): void {
    if (!this.alive) return;
    this.alive = false;
    this.d.room.me.alive = false;
    this.recoil.reset();
    this.flinch.reset();
    this.camera.startDeath();
    this.d.audio.setLowHealth(false);
    this.d.audio.playDeath();
    this.d.input.discardMouse();
    this.health = 0;
  }

  // ---- Snapshots ----

  /**
   * The ping/pong clock knows how fast the server's ticks run against ours: tell the lead controller
   * once per connection, and again when the pongs say the rate moved by more than 1 % (the clock of a
   * VM changes rate after the host sleeps).
   */
  private seedClockRate(conn: GameConnection): void {
    const rate = conn.clock.rate();
    if (rate === null) return;
    if (this.rateSeededFor === conn && Math.abs(rate - this.seededRate) < RATE_RESEED) return;
    this.d.tickClock.seedRate(rate);
    this.rateSeededFor = conn;
    this.seededRate = rate;
  }

  /** A snapshot arrived: remember the ack tick and reconcile the prediction. */
  onSnapshot(s: SnapshotData, nowMs: number, conn: GameConnection): void {
    this.lastSnapshotTick = s.tick;
    const local = s.local;
    const predictor = this.predictor;
    if (!this.alive || !predictor || !local || s.tick < this.spawnTick) return;
    this.health = local.health;
    this.spawnProtected = local.spawnProtected;
    this.seedClockRate(conn);
    this.d.tickClock.onSnapshot(s.inputLead, s.inputSlack);
    try {
      const r = predictor.reconcile(s.tick, local.sim);
      const err = Math.hypot(r.errorX, r.errorY, r.errorZ);
      const outcome = r.corrected ? SNAPSHOT_OUTCOME.corrected : r.adopted ? SNAPSHOT_OUTCOME.adopted : SNAPSHOT_OUTCOME.match;
      this.trace.pushSnapshot(nowMs, s.tick, s.inputLead, s.inputSlack, r.latestBefore, outcome, r.corrected ? err : 0);
      if (r.corrected) {
        const v = predictor.view();
        this.view = v;
        this.camera.correct(r.errorX, r.errorY, r.errorZ, v);
        this.trace.pushCorrection(
          nowMs,
          v.tick,
          r.errorX,
          r.errorY,
          r.errorZ,
          s.tick,
          s.tick + s.inputLead,
          conn.clock.ready() ? conn.clock.rttMs() : null,
        );
        conn.recordCorrection(err, nowMs);
      }
    } catch {
      // Malformed sim bytes: keep predicting, the next snapshot will correct.
    }
  }

  /**
   * The collision world changed under ticks already predicted (a glass pane broke): replay them from
   * the newest confirmed tick, so the next snapshot finds nothing to correct; the jump is smoothed
   * like a correction.
   */
  onWorldChanged(): void {
    const predictor = this.predictor;
    if (!this.alive || !predictor) return;
    const r = predictor.replay();
    if (r.replayed === 0) return;
    const v = predictor.view();
    this.view = v;
    this.camera.correct(r.errorX, r.errorY, r.errorZ, v);
  }

  // ---- Frame ----

  /** Look, recoil, fixed-step prediction and input sending for one rendered frame. */
  step(nowMs: number, dt: number, renderTick: number, conn: GameConnection): void {
    const view = this.view;
    const predictor = this.predictor;
    if (!this.alive || !predictor || !view) return;
    const input = this.d.input;
    const scoped = this.d.renderer.viewmodel.scopeActive();
    input.applyMouse(view.adsFrac, view.adsFovMult, scoped);
    this.recoil.compensate(input.deltaPitchUpDeg, input.deltaYawRightDeg);
    this.recoil.step(dt, this.stats.get(view.weaponId).recoveryRate, this.recoilStep);
    input.addView(this.recoilStep.pitch, this.recoilStep.yaw);
    this.flinch.step(dt);

    const clock = this.d.tickClock;
    const n = clock.advance(nowMs);
    if (n === 0) return;
    const first = clock.batchFirstTick;
    // Prediction time minus the time the remote entities are shown at, in ticks (the wire unit is 1/256 tick in a u16, so at
    // most 255 ticks: 2.1 s). Identical for all ticks of the frame.
    const offsetTicks = this.d.snapshots.latest() ? clamp(first + n - 1 + clock.alpha(nowMs) - renderTick, 0, 255) : 0;
    const viewOffset = Math.round(offsetTicks * 256);
    this.frameOffsetTicks = viewOffset / 256;
    probe?.hitreg.beginFrame(this.frameOffsetTicks, this.d.snapshots.latest()?.tick ?? 0, conn.stats.pingMs);
    const durMs = clock.tickDurationMs();
    // Nothing moves or shoots in the freeze time of a Defusal round; the server clears these buttons too.
    const frozen = this.d.room.phase === "round_freeze";
    for (let i = 0; i < n; i++) {
      const tick = first + i;
      const cmd = this.ring[tick % RING] as InputCmdData;
      input.sample(tick, viewOffset, clock.tickStartMs(tick), durMs, cmd);
      if (frozen) freezeInput(cmd);
      const frame = predictor.step(cmd);
      // A tick the server already confirmed is not simulated: no new state, no sounds, no effects.
      this.trace.pushTick(nowMs, frame.view, frame.moveEvents, cmd, !frame.simulated);
      if (frame.simulated) this.onPredicted(frame, cmd, nowMs);
      this.recent.push(cmd);
      if (this.recent.length > MAX_INPUTS_PER_PACKET) this.recent.shift();
    }
    this.sendInputs(conn);
  }

  private sendInputs(conn: GameConnection): void {
    const out = this.scratch;
    out.length = 0;
    const recent = this.recent;
    for (let i = recent.length - 1; i >= 0 && out.length < MAX_INPUTS_PER_PACKET; i--) {
      const c = recent[i] as InputCmdData;
      const newer = out[out.length - 1];
      if (newer && c.tick !== newer.tick - 1) break;
      // Ticks the server already simulated are not needed again (the newest is always sent).
      if (newer && c.tick <= this.lastSnapshotTick) break;
      out.push(c);
    }
    if (out.length === 0) return;
    out.reverse();
    conn.sendInputs(this.lastSnapshotTick, out);
  }

  // ---- Predicted frame effects ----

  private onPredicted(frame: PredictedFrame, cmd: InputCmdData, nowMs: number): void {
    const view = frame.view;
    this.view = view;
    this.camera.push(view);
    const audio = this.d.audio;

    const m = frame.moveEvents;
    if (m.jumped || m.wallJumped || m.jumpPad) audio.playMovement("jump");
    if (m.landed !== null) {
      audio.playMovement(m.landed > HEAVY_LAND_SPEED ? "landHeavy" : "land");
      this.camera.dip.land(m.landed);
    }
    if (m.slideStarted) audio.playMovement("slideStart");
    if (this.prevSliding && !view.sliding) audio.playMovement("slideStop");
    this.prevSliding = view.sliding;
    if (m.wallrunStarted || m.climbStarted) audio.playMovement("wallrunStart");
    if (m.mantled) audio.playMovement("mantle");
    // Movement cues: the crosshair flash and sound of a perfectly timed hop, slide reset or wall hop (sloppy timing
    // gets none), a short FOV pulse for perfect hops and resets, every wall jump, climbs and ledge recoveries.
    if (m.perfectHop) {
      audio.playMovement("perfectHop");
      this.d.hud.perfectHop();
      if (!m.wallJumped) this.camera.pulseFov(m.slideReset ? FOV_PULSE_DEG.slideReset : FOV_PULSE_DEG.perfectHop);
    }
    if (m.wallJumped) this.camera.pulseFov(FOV_PULSE_DEG.wallJump);
    if (m.climbStarted) this.camera.pulseFov(FOV_PULSE_DEG.climb);
    if (m.ledgeRecovered) this.camera.pulseFov(FOV_PULSE_DEG.ledgeRecover);
    this.footsteps(view, cmd);

    for (const shot of frame.shots) this.onShot(shot, view, cmd.tick);

    const w = frame.weaponEvents;
    if (w.reloadStarted) {
      audio.playReload(view.action === "reload_shell" ? "shell" : "magOut");
      this.magInPlayed = false;
    }
    if (this.shellCue.update(view.slot, view.ammo, view.action)) audio.playReload("shell");
    if (w.reloadFinished) audio.playReload("rack");
    if (w.switched !== null) audio.playReload("equip");
    if (w.dryFire) audio.playReload("dry");
    if (w.melee) {
      this.meleeStart = nowMs;
      audio.playReload("knifeSwing");
    }
    if (w.throws.length > 0) {
      this.throwStart = nowMs;
      audio.playGrenade("throw");
    }
    if (view.reloading && !this.magInPlayed && view.action === "reload" && view.reloadFrac >= MAG_IN_FRAC) {
      this.magInPlayed = true;
      audio.playReload("magIn");
    }
  }

  private onShot(shot: PredictedShot, view: PlayerView, tick: number): void {
    const w = this.stats.get(shot.weaponId);
    this.recoil.kick(shot.kickPitchDeg, shot.kickYawDeg, w.recoveryDelay);
    this.flinch.kick(shot.kickPitchDeg);
    this.shotsThisFrame++;
    this.shotKickThisFrame = Math.max(this.shotKickThisFrame, shot.kickPitchDeg);
    // The tracer and flash start at the muzzle as drawn this frame: they wait until the camera and
    // the viewmodel have been updated (`updateViewmodel`).
    this.pendingShots.push({
      shot,
      tick,
      // The server rewinds a shot of tick T at sub-tick f to `T - 1 + f - view offset`.
      remoteTick: tick - 1 + shot.subtick - this.frameOffsetTicks,
      seed: shotSeed(this.d.room.matchSeed, this.d.room.myId, shot.shotCounter),
      hideFlash: view.hideFlash,
    });
  }

  private footsteps(view: PlayerView, cmd: InputCmdData): void {
    const x = view.pos.x;
    const z = view.pos.z;
    const dist = this.haveStep ? Math.hypot(x - this.lastStepX, z - this.lastStepZ) : 0;
    this.lastStepX = x;
    this.lastStepZ = z;
    this.haveStep = true;
    if (!view.onGround || view.sliding || view.mantling || dist <= 0.001) {
      this.stride.reset();
      return;
    }
    if (!this.stride.feed(dist)) return;
    const volume = ownStepVolume((cmd.buttons & Buttons.WALK) !== 0, view.crouched);
    if (volume > 0) this.d.audio.playFootstep(this.d.fx.surfaceUnder(view.pos), view.pos, volume);
  }

  // ---- Render-time data ----

  /** Camera pose of the living player. */
  pose(dt: number, nowMs: number): CameraPose {
    const view = this.view as PlayerView;
    const s = settings.value.video;
    const pose = this.camera.update(dt, this.d.tickClock.alpha(nowMs), view, {
      yaw: this.d.input.yaw,
      pitch: this.d.input.pitch,
      baseFovDeg: s.fov,
      dynamicFov: s.dynamicFov,
      runSpeed: this.runSpeed,
      hopSpeedCap: this.hopSpeedCap,
    });
    pose.roll += this.flinch.rollDeg * DEG2RAD;
    return pose;
  }

  /** Updates the viewmodel for this frame. */
  updateViewmodel(nowMs: number, dt: number, visible: boolean): void {
    const view = this.view;
    const vm = this.vm;
    const input = this.d.input;
    if (view) {
      vm.weaponId = view.weaponId;
      vm.attachments = this.stats.attachmentsOf(view.weaponId);
      vm.camo = this.stats.camoOf(view.weaponId);
      vm.adsFrac = view.adsFrac;
      vm.reloadFrac = view.reloadFrac;
      vm.switchFrac = view.switchingFrac;
      vm.speed = view.speed;
      vm.onGround = view.onGround;
      vm.sliding = view.sliding;
      vm.wallrunSide = view.wallrunSide;
    }
    vm.visible = visible && this.alive && view !== null && settings.value.video.showViewmodel;
    vm.shotsThisFrame = this.shotsThisFrame;
    vm.shotKickDeg = this.shotKickThisFrame;
    vm.kickPitchDeg = this.recoil.uncompPitchDeg;
    vm.kickYawDeg = this.recoil.uncompYawDeg;
    vm.lookDeltaX = input.deltaYawRightDeg;
    vm.lookDeltaY = input.deltaPitchUpDeg;
    vm.meleeFrac = this.animFrac(this.meleeStart, nowMs, MELEE_ANIM_MS, true);
    vm.throwFrac = this.animFrac(this.throwStart, nowMs, THROW_ANIM_MS, false);
    this.shotsThisFrame = 0;
    this.shotKickThisFrame = 0;
    input.endFrame();
    this.d.renderer.viewmodel.update(vm, dt);
    for (const p of this.pendingShots) {
      const w = this.stats.get(p.shot.weaponId);
      probe?.hitreg.volley(p.tick, p.shot.subtick, p.remoteTick);
      this.d.fx.localShot(p.shot, this.stats.attachmentsOf(p.shot.weaponId), p.seed, p.hideFlash, this.d.room.myId, w.fireInterval, p.remoteTick);
    }
    this.pendingShots.length = 0;
  }

  private animFrac(start: number, nowMs: number, lengthMs: number, melee: boolean): number {
    if (start < 0) return 0;
    const f = (nowMs - start) / lengthMs;
    if (f >= 1) {
      if (melee) this.meleeStart = -1;
      else this.throwStart = -1;
      return 0;
    }
    return Math.max(0.001, f);
  }

  /** Fills and submits the HUD frame; fps and ping are refreshed four times a second. */
  updateHud(nowMs: number, conn: GameConnection, hFovDeg: number, spectating: boolean): void {
    const f = this.hudFrame;
    const view = this.view;
    if (nowMs - this.hudInfoAt > 250) {
      this.hudInfoAt = nowMs;
      this.hudFps = this.d.renderer.info().fps;
      this.hudPing = Math.round(conn.clock.rttMs());
    }
    const scoped = this.d.renderer.viewmodel.scopeActive();
    if (scoped !== this.scopeShown) {
      this.scopeShown = scoped;
      this.d.hud.scope(scoped);
    }
    f.health = this.alive ? this.health : 0;
    if (view) {
      f.ammo = view.ammo;
      f.reserve = view.reserve;
      f.magSize = view.magSize;
      f.weaponName = weaponName(view.weaponId);
      f.slot = view.slot;
      f.reloading = view.reloading;
      f.reloadFrac = view.reloadFrac < 0 ? 0 : view.reloadFrac;
      f.speed = view.speed;
      f.spreadDeg = view.spreadDeg;
      f.adsFrac = view.adsFrac;
      f.lethalCount = view.lethalLeft;
      f.tacticalCount = view.tacticalLeft;
    }
    f.lethal = this.loadout.lethal;
    f.tactical = this.loadout.tactical;
    f.hFovDeg = hFovDeg;
    f.fps = this.hudFps;
    f.pingMs = this.hudPing;
    f.showCrosshair = this.alive && !spectating && !scoped;
    f.reticleDot = view !== null && view.adsFrac >= 0.6 && hasDotSight(view.weaponId, this.stats.attachmentsOf(view.weaponId));
    const room = this.d.room;
    // A Defusal freeze moves the start of the protection to its (possibly extended) end.
    if (room.phase === "round_freeze") this.shieldStartTick = shieldStart(this.spawnTick, room.phase, room.phaseEndTick);
    f.spawnShield = spawnShieldLeft(
      this.alive && this.spawnProtected,
      this.shieldStartTick,
      conn.clock.estServerTick(nowMs),
      room.rules?.spawnProtectionSec ?? 0,
      TICK_RATE,
    );
    this.d.hud.update(f);
  }

  /** Whether low-health audio should run. */
  lowHealth(): boolean {
    return this.alive && this.health > 0 && this.health <= 30;
  }
}
